import React from 'react';
import { ArrowUpRightIcon, CheckIcon } from 'lucide-react';
import { SectionHeader } from '../components/ui/SectionHeader';
import { LayerStack } from '../components/scenes/LayerStack';
import { ModulesShowcase } from '../components/sections/ModulesShowcase';
import { ClosingInvitation } from '../components/sections/ClosingInvitation';
import { workspaces } from '../data/workspaces';
import { modules } from '../data/modules';
import { SYNEURAL_HCF_URL, SYNEURAL_SKP_URL } from '../data/navigation';
const layers = [{
  name: 'Noetivra Core',
  body: 'The shared foundation connecting modules and configured workspaces: identity and roles, records, activity history, and configuration.'
}, {
  name: 'Noetivra Workspace',
  body: 'The visual environment where people explore information, complete tasks, review findings, and take action.'
}, {
  name: 'Connected Modules',
  body: 'Knowledge, Capability, Services, and Credentials, brought together according to the organization’s needs.'
}];
const technology = [{
  name: 'Semantic Knowledge Platform',
  body: 'Connects and governs knowledge, evidence, terminology, and provenance.',
  href: SYNEURAL_SKP_URL
}, {
  name: 'Human Capability Framework',
  body: 'Defines assessment and development methods used where relevant.',
  href: SYNEURAL_HCF_URL
}];
export function HowItWorks() {
  return <>
      <section className="pb-20 pt-14 md:pb-28 md:pt-20" data-id="element-681" data-mp="191yiqc2izrmn">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 md:px-8 lg:grid-cols-2" data-id="element-682" data-mp="142c4q0evi7cs">
          <div data-id="element-683" data-mp="1007dt9ft7otu">
            <SectionHeader as="h1" title="Three layers. One clear experience." body="Noetivra keeps the complexity underneath. People work in a familiar workspace, configured from connected modules on a shared core." data-id="element-684" data-mp="zzz39o75svlt" />
            <dl className="mt-10 space-y-6" data-id="element-685" data-mp="2pff84re1vv5w">
              {layers.map(l => <div key={l.name} className="border-l-2 border-accent/30 pl-5" data-id="element-686" data-mp="2pdc6fa3jcdlc">
                  <dt className="text-lg font-semibold tracking-tight" data-id="element-687" data-mp="38igz4uuo9p0g">{l.name}</dt>
                  <dd className="mt-1 text-[15px] leading-relaxed text-ink-soft" data-id="element-688" data-mp="3dbjzpmxv9yq5">{l.body}</dd>
                </div>)}
            </dl>
          </div>
          <div className="bg-dots rounded-[32px] px-4 py-14 sm:px-10" data-id="element-689" data-mp="34i6v3nqfx8o8">
            <LayerStack data-id="element-690" data-mp="34gc7g1nsb2gg" />
          </div>
        </div>
      </section>

      <div className="bg-white/50" data-id="element-691" data-mp="3s8j1uv9y8zd8">
        <ModulesShowcase id="modules" title="Connected modules, configured to fit." body="Each organization uses the modules relevant to its work. Research and clinical workflows do not depend on civic assessment, and a referral service needs no knowledge curation." data-id="element-692" data-mp="3s66jih0a4mzn" />
      </div>

      <section className="py-24 md:py-32" data-id="element-693" data-mp="26uy8fpepdtkn">
        <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-694" data-mp="26t3k01483jbt">
          <SectionHeader title="Modules by workspace." body="Typical configurations. Each organization can adjust its own." data-id="element-695" data-mp="2buars06d8iih" />
          <div className="glass mt-12 overflow-x-auto rounded-[24px]" data-id="element-696" data-mp="24973pad6lnxs">
            <table className="w-full min-w-[640px] text-left" data-id="element-697" data-mp="1zbourjz5z8ps">
              <caption className="sr-only" data-id="element-698" data-mp="1zd9q3btayvop">Modules typically used by each workspace</caption>
              <thead data-id="element-699" data-mp="2d8u9s9guvhx6">
                <tr className="border-b border-ink/[0.07] text-sm text-ink-muted" data-id="element-700" data-mp="28bu2jv89s94h">
                  <th scope="col" className="px-6 py-4 font-medium" data-id="element-701" data-mp="28a0v4dpu1kvf">Workspace</th>
                  {modules.map(m => <th key={m.id} scope="col" className="px-6 py-4 text-center font-medium" data-id="element-702" data-mp="1gkxrn05rseju">
                      {m.name}
                    </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/[0.06]" data-id="element-703" data-mp="1pku84ei0a73t">
                {workspaces.map(w => <tr key={w.slug} data-id="element-704" data-mp="11uzy04p2mwn9">
                    <th scope="row" className="px-6 py-4 text-[15px] font-semibold" data-id="element-705" data-mp="11tyldvseq23f">{w.name}</th>
                    {modules.map(m => {
                  const has = w.modules.includes(m.id);
                  return <td key={m.id} className="px-6 py-4 text-center" data-id="element-706" data-mp="1fii7e74larlx">
                          {has ? <CheckIcon className="mx-auto h-4 w-4 text-accent" aria-label="Included" data-id="element-707" data-mp="1khuzp7e8cqvw" /> : <span className="text-ink-faint" aria-label="Not required" data-id="element-708" data-mp="1p93bub6y2qcr">–</span>}
                        </td>;
                })}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32" data-id="element-709" data-mp="238ujzvas5ciu">
        <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-710" data-mp="287pevvm0urjb">
          <SectionHeader title="Powered by SyNeural technology." body="SyNeural develops the underlying systems. Noetivra is how people use them." data-id="element-711" data-mp="286e8rp53t7p1" />
          <div className="mt-12 grid gap-4 md:grid-cols-2" data-id="element-712" data-mp="dtog1aqh24j8">
            {technology.map(t => <a key={t.name} href={t.href} target="_blank" rel="noopener noreferrer" className="group flex flex-col rounded-[28px] bg-ink p-8 text-white transition-colors duration-200 ease-out hover:bg-ink-soft md:p-10" data-id="element-713" data-mp="2r4za0mey780z">
                <p className="text-sm text-white/55" data-id="element-714" data-mp="2d9wy9inf6og9">SyNeural</p>
                <h3 className="mt-2 text-2xl font-semibold tracking-tight" data-id="element-715" data-mp="3a18upskcuwty">{t.name}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-white/75" data-id="element-716" data-mp="360f770atrk1l">{t.body}</p>
                <span className="mt-auto inline-flex items-center gap-1 pt-8 text-sm font-medium" data-id="element-717" data-mp="3112l4hrs2h02">
                  Read more on syneural.com
                  <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:-translate-y-px group-hover:translate-x-px" aria-hidden="true" data-id="element-718" data-mp="2w29b4zbp984n" />
                </span>
                <span className="sr-only" data-id="element-719" data-mp="1klrcqtojkvfd">(opens in a new tab)</span>
              </a>)}
          </div>
        </div>
      </section>

      <ClosingInvitation title="See the layers at work." body="Every interactive demo runs on the same foundation, configured for a different field." primary={{
      label: 'Explore Demos',
      to: '/demos'
    }} secondary={{
      label: 'Discuss Your Workspace',
      to: '/contact'
    }} data-id="element-720" data-mp="1r3m7lnat7z2l" />
    </>;
}